import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import {
  PaginationResponse,
  OrderByDirection,
  SearchPossibleEventOwnersRequest,
  FetchEventsResponse,
  SearchPossibleEventOwnersEventTypesEnum,
} from '../swagger'
import { events as eventsApi } from '../api/swagger'
import { SnackbarSeverity } from '../components/Context/SnackbarContext'
import { useErrorSnackbar } from './useErrorSnackbar'
import { useLoadingIds } from './useLoadingIds'
import useLoadingContext from './useLoadingContext'

interface PotentialOwner {
  fullName: string
  roleName: string
  actorKey: number
}

interface UseEventReassignmentProps {
  onReassignComplete?: (pagination: PaginationResponse) => void
  currentPage: number
  pageSize: number
  totalCount: number
  events: FetchEventsResponse[]
}

interface UseEventReassignmentReturn {
  selectedRows: GridRowSelectionModel
  selectedEventTypes: string[]
  isReassignModalOpen: boolean
  selectedOwner: PotentialOwner | null
  isReassigning: boolean
  ownerOptions: PotentialOwner[]
  isLoadingOwners: boolean
  possibleOwnerSearchInput: string
  handleSelectionChange: (selectedRowIds: GridRowSelectionModel) => void
  handleOpenReassignModal: () => void
  handleCloseReassignModal: () => void
  handleReassignOwners: () => Promise<void>
  setSelectedOwner: (owner: PotentialOwner | null) => void
  setOwnerSearchInput: (input: string) => void
}

export const useEventReassignment = ({
  onReassignComplete,
  currentPage,
  pageSize,
  totalCount,
  events,
}: UseEventReassignmentProps): UseEventReassignmentReturn => {
  const { t } = useTranslation()
  const {
    handleError,
    setSnackbarMessage,
    setSnackbarState,
    setSnackbarSeverity,
  } = useErrorSnackbar()

  // State
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([])
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([])
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<PotentialOwner | null>(
    null
  )
  const [ownerOptions, setOwnerOptions] = useState<PotentialOwner[]>([])
  const [ownersSearchInput, setOwnersSearchInput] = useState('')

  const {
    Events: { searchPossibleEventOwners, bulkReassignEvents },
  } = useLoadingIds()

  // Helper function to get unique event types from selected events
  const getEventTypesFromSelection = useCallback(
    (selectedRowIds: GridRowSelectionModel): string[] => {
      if (!selectedRowIds.length || !events.length) return []

      const selectedIndices = selectedRowIds.map((id) => Number(id))
      const selectedEvents = selectedIndices
        .filter((index) => index >= 0 && index < events.length)
        .map((index) => events[index])

      const uniqueEventTypes = [
        ...new Set(selectedEvents.map((event) => event.eventType)),
      ]
      return uniqueEventTypes
    },
    [events]
  )

  // Update event types whenever selection changes
  useEffect(() => {
    const eventTypes = getEventTypesFromSelection(selectedRows)
    setSelectedEventTypes(eventTypes)
  }, [selectedRows, getEventTypesFromSelection])

  // Fetch possible event owners function
  const fetchPossibleEventOwnersFunction = useCallback(async () => {
    try {
      const params: SearchPossibleEventOwnersRequest = {
        orderBy: ['lastName ASC'],
        eventTypes:
          selectedEventTypes as SearchPossibleEventOwnersEventTypesEnum[],
      }

      // Only add search param if it's provided and has at least 3 characters
      if (ownersSearchInput && ownersSearchInput.length >= 3) {
        params.search = ownersSearchInput
      }

      const response = await eventsApi.searchPossibleEventOwners(params)

      const validPossibleOwners =
        (response.possibleOwners?.map((owner) => ({
          fullName: owner.fullName,
          roleName: owner.roleName,
          actorKey: owner.actorKey,
        })) as PotentialOwner[]) || []

      // Only update options if we have results or if we're clearing the search
      if (
        validPossibleOwners.length > 0 ||
        !ownersSearchInput ||
        ownersSearchInput === ''
      ) {
        setOwnerOptions(validPossibleOwners)
      }
    } catch (e) {
      await handleError(e)
    }
  }, [handleError, selectedEventTypes, ownersSearchInput])

  // Use useLoadingContext for fetching possible owners
  const {
    isLoading: isLoadingOwners,
    triggerFetch: triggerFetchPossibleOwners,
  } = useLoadingContext({
    asyncFunction: fetchPossibleEventOwnersFunction,
    loadingId: searchPossibleEventOwners,
  })

  // Event handlers
  const handleSelectionChange = (selectedRowIds: GridRowSelectionModel) => {
    setSelectedRows(selectedRowIds)
  }

  const handleOpenReassignModal = useCallback(() => {
    if (selectedRows.length === 0) {
      setSnackbarMessage(
        t(
          'Event.Reassign.NoSelection',
          'Please select at least one event to reassign.'
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Warning)
      setSnackbarState(true)
      return
    }
    setIsReassignModalOpen(true)
  }, [
    selectedRows,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
    t,
  ])

  const handleCloseReassignModal = useCallback(() => {
    setIsReassignModalOpen(false)
    setSelectedOwner(null)
    setOwnersSearchInput('')
  }, [])

  // Bulk reassign events function
  const bulkReassignEventsFunction = useCallback(async () => {
    if (!selectedOwner) {
      throw new Error(
        t('Event.Reassign.NoOwner', 'Please select an owner to reassign to.')
      )
    }

    const newEventOwnerKey = selectedOwner.actorKey

    if (!newEventOwnerKey) {
      throw new Error(
        t(
          'Event.Reassign.Error.NoOwnerRole',
          'The selected owner does not have the correct permissions.'
        )
      )
    }

    // Convert selected row indices to actual eventKeys
    const eventKeysToReassign = selectedRows
      .map((rowId) => {
        const index = Number(rowId)
        return index >= 0 && index < events.length
          ? events[index].eventKey
          : null
      })
      .filter((eventKey): eventKey is number => eventKey !== null)

    await eventsApi.bulkReassignEvents({
      body: {
        newEventOwnerKey,
        eventKeysToReassign,
      },
    })

    // Success handling
    setSnackbarMessage(
      t(
        'Event.Reassign.Success',
        'Events successfully reassigned to the new owner.'
      )
    )
    setSnackbarSeverity(SnackbarSeverity.Success)
    setSnackbarState(true)

    // Reset state
    handleCloseReassignModal()
    setSelectedRows([])

    if (onReassignComplete) {
      onReassignComplete({
        page: currentPage,
        pageSize: pageSize,
        totalCount: totalCount,
        orderBy: [{ startDate: OrderByDirection.Asc }],
      })
    }
  }, [
    selectedOwner,
    selectedRows,
    events,
    t,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
    onReassignComplete,
    currentPage,
    pageSize,
    totalCount,
    handleCloseReassignModal,
  ])

  // Use useLoadingContext for bulk reassign
  const { isLoading: isReassigning, triggerFetch: triggerBulkReassign } =
    useLoadingContext({
      asyncFunction: bulkReassignEventsFunction,
      loadingId: bulkReassignEvents,
      callback: () => {
        // Any additional cleanup can go here if needed
      },
    })

  const handleReassignOwners = useCallback(async () => {
    if (!selectedOwner) {
      setSnackbarMessage(
        t('Event.Reassign.NoOwner', 'Please select an owner to reassign to.')
      )
      setSnackbarSeverity(SnackbarSeverity.Warning)
      setSnackbarState(true)
      return
    }

    try {
      triggerBulkReassign()
    } catch (e) {
      await handleError(e)
    }
  }, [
    selectedOwner,
    triggerBulkReassign,
    handleError,
    t,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
  ])

  // Effect to trigger fetching owners when search input or event types change
  useEffect(() => {
    if (ownersSearchInput.length >= 3) {
      triggerFetchPossibleOwners()
    } else if (
      selectedEventTypes.length > 0 &&
      ownersSearchInput.length === 0
    ) {
      // Only fetch supervisors when event types change AND there's no search input
      triggerFetchPossibleOwners()
    }
  }, [ownersSearchInput, selectedEventTypes, triggerFetchPossibleOwners])

  return {
    selectedRows,
    selectedEventTypes,
    isReassignModalOpen,
    selectedOwner,
    isReassigning,
    ownerOptions,
    isLoadingOwners,
    possibleOwnerSearchInput: ownersSearchInput,
    handleSelectionChange,
    handleOpenReassignModal,
    handleCloseReassignModal,
    handleReassignOwners,
    setSelectedOwner,
    setOwnerSearchInput: setOwnersSearchInput,
  }
}

export default useEventReassignment
